import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";
import FormSection from "@ats/src/components/forms/FormSection";
import PlanBadge from "@ats/src/components/shared/PlanBadge";
import { useFeatureGate } from "@shared/hooks/useFeatureGate";
import { PlanFeatures } from "@shared/types/organization";

interface DistributionSectionProps {
  savedCountry: string | null;
  savedStateRegion: string | null;
  savedCity: string | null;
  savedRemoteness: string;
  savedStatus: string;
  formCountry: string | null;
  formStateRegion: string | null;
  formCity: string | null;
  formRemoteness: string;
  orgLinkedInEnabled: boolean;
  jobLinkedInEnabled: boolean;
  jobId: number;
  jobRemotenessNames: (remoteness: string) => string;
}

function DistributionSection({
  savedCountry,
  savedStateRegion,
  savedCity,
  savedRemoteness,
  savedStatus,
  formCountry,
  formStateRegion,
  formCity,
  formRemoteness,
  orgLinkedInEnabled,
  jobLinkedInEnabled,
  jobId,
  jobRemotenessNames,
}: DistributionSectionProps) {
  const { getFeatureInfo } = useFeatureGate();
  const feature = getFeatureInfo(PlanFeatures.LINKEDIN_FEED);
  const isLive = savedStatus === "published";

  const hasValue = (val) => val?.length > 0;

  // Form state validation (current state user sees)
  const isUS = formCountry === "US";
  const isRemoteOnly = formRemoteness === "remoteness_only";

  const isValid = isRemoteOnly
    ? hasValue(formCountry)
    : isUS
      ? hasValue(formCountry) && hasValue(formStateRegion) && hasValue(formCity)
      : hasValue(formCountry) && hasValue(formCity);

  // Check for any changes to location/remoteness fields
  const hasChanges =
    savedCountry !== formCountry ||
    savedStateRegion !== formStateRegion ||
    savedCity !== formCity ||
    savedRemoteness !== formRemoteness;

  // Status message
  let statusMessage;
  if (hasChanges) {
    statusMessage = isValid
      ? "Will be distributed after saving"
      : "Will not be distributed after saving";
  } else {
    statusMessage = isValid
      ? isLive ? "Distributing to LinkedIn" : "Ready for distribution"
      : isLive ? "Not being distributed to LinkedIn" : "Not ready for distribution";
  }

  const showAsReady = isValid;
  const showAsIssue = !isValid;

  // Button link logic
  const buttonLink = feature.isLocked || !orgLinkedInEnabled
    ? { pathname: "/hire/settings/integrations/linkedin", state: { orginalPathname: location.pathname } }
    : `/jobs/${jobId}/distribution/linkedin`;

  // Determine config
  let item;

  if (feature.isLocked) {
    item = {
      iconName: "alert-circle",
      showAsIssue: false,
      showAsReady: false,
      statusMessage: "Not enabled for your organization",
      showRequirements: false,
      buttonLabel: "",
      showBadge: true,
    };
  } else if (!orgLinkedInEnabled) {
    item = {
      iconName: "alert-circle",
      showAsIssue: false,
      showAsReady: false,
      statusMessage: "Not enabled for your organization",
      showRequirements: false,
      buttonLabel: "Enable in settings",
      showBadge: false,
    };
  } else if (!jobLinkedInEnabled) {
    item = {
      iconName: "alert-circle",
      showAsIssue: true,
      showAsReady: false,
      statusMessage: "Disabled for this job",
      showRequirements: false,
      buttonLabel: "Manage",
      showBadge: false,
    };
  } else if (isValid) {
    item = {
      iconName: "check-circle",
      showAsIssue: false,
      showAsReady: true,
      statusMessage,
      showRequirements: false,
      buttonLabel: "",
      showBadge: false,
    };
  } else {
    item = {
      iconName: showAsReady ? "check-circle" : "alert-circle",
      showAsIssue,
      showAsReady,
      statusMessage,
      showRequirements: !isValid,
      buttonLabel: "",
      showBadge: false,
    };
  }

  return (
    <FormSection title="Distribution">
      <Styled.Description>
        Check the status of external job board integrations below.
      </Styled.Description>

      <Styled.IntegrationList>
        <Styled.Integration>
          <Styled.IntegrationContent>
            <Styled.IntegrationWithIcon>
              <Styled.IntegrationIcon hasIssue={item.showAsIssue} isReady={item.showAsReady}>
                <Icon name={item.iconName} />
              </Styled.IntegrationIcon>
              <Styled.IntegrationTextColumn>
                {item.showBadge ? (
                  <Styled.IntegrationNameWithBadge>
                    <Styled.IntegrationName>LinkedIn</Styled.IntegrationName>
                    <PlanBadge
                      planKey={feature.upgradePlanKey}
                      planDisplayName={feature.planDisplayName}
                      locked={feature.isLocked}
                      size="small"
                      includeUrl={true}
                      feature={PlanFeatures.LINKEDIN_FEED}
                    />
                  </Styled.IntegrationNameWithBadge>
                ) : (
                  <Styled.IntegrationName>LinkedIn</Styled.IntegrationName>
                )}
                <Styled.IntegrationStatus hasIssue={item.showAsIssue} isReady={item.showAsReady}>
                  {item.statusMessage}
                </Styled.IntegrationStatus>
                {item.showRequirements && (
                  <>
                    <Styled.RequirementsIntro>
                      Your job is set to <strong>{jobRemotenessNames(formRemoteness)}</strong>. To qualify for LinkedIn, update <strong>Hiring location</strong>:
                    </Styled.RequirementsIntro>
                    <Styled.RequirementsList>
                      {!hasValue(formCountry) && <li>Add a country</li>}
                      {!isRemoteOnly && isUS && !hasValue(formStateRegion) && <li>Add a state</li>}
                      {!isRemoteOnly && !hasValue(formCity) && <li>Add a city</li>}
                    </Styled.RequirementsList>
                  </>
                )}
              </Styled.IntegrationTextColumn>
            </Styled.IntegrationWithIcon>
          </Styled.IntegrationContent>
          {item.buttonLabel && (
            <Button type="internalLink" link={buttonLink} styleType="text">{item.buttonLabel}</Button>
          )}
        </Styled.Integration>
      </Styled.IntegrationList>
    </FormSection>
  );
}

export default DistributionSection;

// ============================================
// Styled Components
// ============================================

let Styled: any = {};

Styled.Description = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_Description;
    ${[t.text.xs, t.mt(-2), t.mb(4)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    line-height: 1.4;
    max-width: 32rem;
  `;
});

Styled.IntegrationList = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_IntegrationList;
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    border-radius: 0.4375rem;
    overflow: hidden;
  `;
});

Styled.Integration = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_Integration;
    ${[t.px(4), t.py(3)]};
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background-color: ${t.dark ? t.color.gray[800] : "transparent"};

    &:not(:last-child) {
      border-bottom: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    }
  `;
});

Styled.IntegrationContent = styled.div(() => {
  return css`
    label: DistributionSection_IntegrationContent;
    flex: 1;
    min-width: 0;
  `;
});

Styled.IntegrationWithIcon = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_IntegrationWithIcon;
    display: flex;
    align-items: flex-start;
    gap: ${t.spacing[2]};
  `;
});

Styled.IntegrationIcon = styled.div<{ hasIssue?: boolean; isReady?: boolean }>((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_IntegrationIcon;
    flex-shrink: 0;
    margin-top: 0.125rem;

    svg {
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      margin-top: -2px;
      color: ${props.hasIssue
        ? t.dark ? t.color.red[500] : t.color.red[600]
        : props.isReady
          ? t.dark ? "#4ADE80" : "#16A34A"
          : t.dark ? t.color.gray[400] : t.color.gray[500]};
    }
  `;
});

Styled.IntegrationTextColumn = styled.div(() => {
  return css`
    label: DistributionSection_IntegrationTextColumn;
    flex: 1;
    min-width: 0;
  `;
});

Styled.IntegrationName = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_IntegrationName;
    ${[t.text.sm, t.text.bold, t.mr("1px")]};
    color: ${t.dark ? t.color.gray[200] : t.color.gray[900]};
  `;
});

Styled.IntegrationNameWithBadge = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_IntegrationNameWithBadge;
    display: flex;
    ${[t.mb(2)]}
  `;
});

Styled.IntegrationStatus = styled.p<{ isReady?: boolean; hasIssue?: boolean }>((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_IntegrationStatus;
    ${[t.text.sm, t.mt(1)]};
    color: ${props.hasIssue
      ? t.dark ? t.color.red[500] : t.color.red[600]
      : props.isReady
        ? t.dark ? "#4ADE80" : "#16A34A"
        : t.dark ? t.color.gray[400] : t.color.gray[500]};
  `;
});

Styled.RequirementsIntro = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_RequirementsIntro;
    ${[t.text.sm, t.mt(2)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
    line-height: 1.4;

    strong {
      color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    }
  `;
});

Styled.RequirementsList = styled.ul((props) => {
  const t: any = props.theme;
  return css`
    label: DistributionSection_RequirementsList;
    ${[t.text.sm, t.mt(2), t.pl(4)]};
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    list-style-type: disc;
    line-height: 1.5;

    li {
      ${t.mb(1)};
    }

    li:last-child {
      margin-bottom: 0;
    }
  `;
});